import React, {forwardRef, useImperativeHandle} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useDisclosure} from '@chakra-ui/react'
import ConfirmationModal from '../../confirmation-modal'

/**
 * Modal that appears when warranty consistency validation fails.
 * Informs the user that the basket contains warranty inconsistencies
 * and all products will be removed.
 *
 * @param {function} handleClearBasket - Callback to clear all products from the basket
 * @returns {JSX.Element} - Confirmation Modal Component
 */

const WarrantyConsistencyErrorModal = forwardRef(({handleClearBasket}, ref) => {
    const {formatMessage} = useIntl()
    const {isOpen, onOpen, onClose} = useDisclosure()

    useImperativeHandle(ref, () => ({
        onOpen,
    }))

    const handleConfirm = async () => {
        onClose()
        await handleClearBasket()
    }

    return (
        <ConfirmationModal
            data-testid="warranty-consistency-modal"
            onAlternateAction={handleConfirm}
            title={formatMessage({
                defaultMessage: 'Warranty Consistency Error',
                id: 'cart.warranty_consistency_modal.error_title',
            })}
            description={formatMessage({
                defaultMessage:
                    'Your basket contains warranty inconsistencies. All products will be removed. Please add items again.',
                id: 'cart.warranty_consistency_modal.error_description',
            })}
            isOpen={isOpen}
        />
    )
})

WarrantyConsistencyErrorModal.propTypes = {
    handleClearBasket: PropTypes.func.isRequired,
}

WarrantyConsistencyErrorModal.displayName = 'WarrantyConsistencyErrorModal'

export default WarrantyConsistencyErrorModal
